import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Plus, Users, ExternalLink, Settings, Clock, ChevronRight, Trash2, LogIn, XCircle } from 'lucide-react';

export default function HomePage() {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [settingsTeam, setSettingsTeam] = useState(null);
  const [projects, setProjects] = useState([]);
  const [actionError, setActionError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.teams.list(),
        api.reports.sessions({ limit: 10 })
      ]);
      setTeams(t);
      setSessions(s);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!authLoading) loadData(); }, [authLoading, loadData]);

  useEffect(() => {
    if (user) {
      api.jira.projects().then(setProjects).catch(console.error);
    }
  }, [user]);

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team and all its sessions? This cannot be undone.')) return;
    try {
      await api.teams.delete(teamId);
      setTeams(t => t.filter(t => t.id !== teamId));
      setSessions(s => s.filter(s => s.team_id !== teamId));
    } catch (e) { setActionError(e.message); }
  };

  const handleTeamUpdate = (updatedTeam) => {
    setTeams(t => t.map(t => t.id === updatedTeam.id ? updatedTeam : t));
    setShowTeamSettings(false);
    setSettingsTeam(null);
  };

  const handleCloseSession = async (sessionId) => {
    try {
      await api.sessions.end(sessionId);
      setSessions(s => s.map(s => s.session_id === sessionId ? { ...s, status: 'completed' } : s));
    } catch (e) { setActionError(e.message); }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Delete this session permanently? This cannot be undone.')) return;
    try {
      await api.sessions.delete(sessionId);
      setSessions(s => s.filter(s => s.session_id !== sessionId));
    } catch (e) { setActionError(e.message); }
  };

  if (loading) return <LoadingPage />;

  return (
    <div className="page">
      {actionError && (
        <div className="toast toast-error mb-4" style={{ cursor: 'pointer' }} onClick={() => setActionError('')}>
          {actionError} ✕
        </div>
      )}

      {/* Hero */}
      <div style={{ marginBottom: 48 }}>
        <div className="flex items-center gap-3 mb-2">
          <h1>Planning Poker</h1>
          {!user && (
            <span className="badge badge-muted">Sign in as facilitator to create sessions</span>
          )}
        </div>
        <p className="text-muted">Estimate smarter. Ship faster.</p>
      </div>

      <div className="layout-two-col">
        {/* Left: Teams & Sessions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 style={{ fontSize: '1.2rem' }}>Teams</h2>
            {user && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewTeam(true)}>
                <Plus size={14} /> New Team
              </button>
            )}
          </div>

          {teams.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🃏</div>
              <p className="text-muted mb-4">No teams yet. Sign in and create your first team to get started.</p>
              {!user && <button className="btn btn-primary" onClick={login}>Sign in with Jira</button>}
            </div>
          ) : (
            <div className="flex-col gap-3">
              {teams.map(team => (
                <TeamCard
                  key={team.id}
                  team={team}
                  user={user}
                  onStartSession={() => { setSelectedTeam(team); setShowNewSession(true); }}
                  onDelete={() => handleDeleteTeam(team.id)}
                  onSettings={() => { setSettingsTeam(team); setShowTeamSettings(true); }}
                  onNavigate={navigate}
                />
              ))}
            </div>
          )}

          {/* Recent sessions */}
          {sessions.length > 0 && (
            <div style={{ marginTop: 40 }}>
              <div className="flex items-center justify-between mb-4">
                <h2 style={{ fontSize: '1.2rem' }}>Recent Sessions</h2>
                {user && (
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/reports')}>
                    View all <ChevronRight size={14} />
                  </button>
                )}
              </div>
              <div className="flex-col gap-2">
                {sessions.map(s => (
                  <SessionRow
                    key={s.session_id}
                    session={s}
                    user={user}
                    onNavigate={navigate}
                    onClose={() => handleCloseSession(s.session_id)}
                    onDelete={() => handleDeleteSession(s.session_id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Quick join */}
        <QuickJoin navigate={navigate} />
      </div>

      {/* Modals */}
      {showNewTeam && (
        <NewTeamModal
          projects={projects}
          onClose={() => setShowNewTeam(false)}
          onCreated={(team) => { setTeams(t => [...t, team]); setShowNewTeam(false); }}
        />
      )}

      {showNewSession && selectedTeam && (
        <NewSessionModal
          team={selectedTeam}
          onClose={() => { setShowNewSession(false); setSelectedTeam(null); }}
          onCreated={(session) => navigate(`/session/${session.id}`)}
        />
      )}

      {showTeamSettings && settingsTeam && (
        <TeamSettingsModal
          team={settingsTeam}
          projects={projects}
          onClose={() => { setShowTeamSettings(false); setSettingsTeam(null); }}
          onSaved={handleTeamUpdate}
        />
      )}
    </div>
  );
}

function TeamCard({ team, user, onStartSession, onDelete, onSettings }) {
  return (
    <div className="card flex items-center gap-4" style={{ padding: '16px 20px' }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10,
        background: 'var(--accent-dim)', border: '1px solid var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.2rem', fontWeight: 800, color: 'var(--accent)', flexShrink: 0
      }}>
        {team.name.charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-bold truncate">{team.name}</div>
        <div className="text-sm text-muted font-mono">{team.jira_project_key}</div>
      </div>
      {user && (
        <div className="flex items-center gap-2">
          <button className="btn btn-primary btn-sm" onClick={onStartSession}>
            <Plus size={14} /> Start Session
          </button>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={onSettings}
            title="Team settings"
            style={{ color: 'var(--text-muted)' }}
          >
            <Settings size={14} />
          </button>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={onDelete}
            title="Delete team"
            style={{ color: 'var(--red)' }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, user, onNavigate, onClose, onDelete }) {
  const isActive = session.status === 'active';
  const statusColor = isActive ? 'badge-green' : 'badge-muted';
  return (
    <div className="card card-sm flex items-center gap-3" style={{ padding: '12px 16px' }}>
      <Clock size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-sm font-bold truncate">{session.session_name}</div>
        <div className="text-xs text-muted">
          {session.team_name} · {session.jira_project_key} · {session.tickets_estimated || 0} tickets
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`badge ${statusColor}`}>{session.status}</span>
        {isActive && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onNavigate(`/session/${session.session_id}`)}
            title="Rejoin session"
          >
            <LogIn size={12} /> Rejoin
          </button>
        )}
        {user && isActive && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            title="Close session"
          >
            <XCircle size={12} /> Close
          </button>
        )}
        {user && (
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={onDelete}
            title="Delete session"
            style={{ color: 'var(--red)' }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function QuickJoin({ navigate }) {
  const [sessionUrl, setSessionUrl] = useState('');

  const handleJoin = () => {
    const id = sessionUrl.split('/').pop();
    if (id) navigate(`/join/${id}`);
  };

  return (
    <div className="card" style={{ position: 'sticky', top: 80 }}>
      <h3 style={{ marginBottom: 16 }}>Join a Session</h3>
      <p className="text-sm text-muted mb-4">Have a session link or ID? Join without an account.</p>
      <div className="input-group">
        <label className="input-label">Session URL or ID</label>
        <input
          className="input"
          placeholder="Paste link or enter ID..."
          value={sessionUrl}
          onChange={e => setSessionUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleJoin()}
        />
      </div>
      <button className="btn btn-primary w-full" onClick={handleJoin} disabled={!sessionUrl.trim()}>
        <Users size={14} /> Join Session
      </button>
      <div className="divider" />
      <p className="text-xs text-muted" style={{ textAlign: 'center' }}>
        You'll be asked for your name when you join
      </p>
    </div>
  );
}

function NewTeamModal({ projects, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [projectName, setProjectName] = useState('');
  const [retroKey, setRetroKey] = useState('RA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name || !projectKey) return;
    setLoading(true);
    try {
      const team = await api.teams.create({
        name,
        jira_project_key: projectKey,
        jira_project_name: projectName,
        retro_jira_project_key: retroKey || 'RA'
      });
      onCreated(team);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Create Team</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="input-group">
          <label className="input-label">Team Name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Backend Squad" autoComplete="off" />
        </div>

        <div className="input-group">
          <label className="input-label">Jira Project</label>
          {projects.length > 0 ? (
            <select className="input" value={projectKey}
              onChange={e => {
                setProjectKey(e.target.value);
                const p = projects.find(p => p.key === e.target.value);
                setProjectName(p?.name || '');
              }}>
              <option value="">Select a project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.key}>{p.name} ({p.key})</option>
              ))}
            </select>
          ) : (
            <input className="input" value={projectKey}
              onChange={e => setProjectKey(e.target.value.toUpperCase())}
              placeholder="e.g. PROJ" />
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Retro Actions Project Key</label>
          <input
            className="input"
            value={retroKey}
            onChange={e => setRetroKey(e.target.value.toUpperCase())}
            placeholder="RA"
          />
          <p className="input-hint">Project key for retro action items (default: RA)</p>
        </div>

        {error && <div className="toast toast-error mb-4">{error}</div>}

        <div className="flex gap-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary w-full" onClick={handleSubmit}
            disabled={loading || !name || !projectKey}>
            {loading ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamSettingsModal({ team, projects, onClose, onSaved }) {
  const [name, setName] = useState(team.name || '');
  const [projectKey, setProjectKey] = useState(team.jira_project_key || '');
  const [projectName, setProjectName] = useState(team.jira_project_name || '');
  const [retroKey, setRetroKey] = useState(team.retro_jira_project_key || 'RA');
  const [slackChannel, setSlackChannel] = useState(team.slack_channel_id || '');
  const [notionDb, setNotionDb] = useState(team.notion_database_id || '');
  const [playbookCtx, setPlaybookCtx] = useState(team.playbook_context || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!name || !projectKey) return;
    setLoading(true);
    try {
      const updated = await api.teams.update(team.id, {
        name,
        jira_project_key: projectKey,
        jira_project_name: projectName,
        retro_jira_project_key: retroKey || 'RA',
        slack_channel_id: slackChannel,
        notion_database_id: notionDb,
        playbook_context: playbookCtx
      });
      onSaved(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>Team Settings</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="input-group">
          <label className="input-label">Team Name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} autoComplete="off" />
        </div>

        <div className="input-group">
          <label className="input-label">Jira Project</label>
          {projects.length > 0 ? (
            <select className="input" value={projectKey}
              onChange={e => {
                setProjectKey(e.target.value);
                const p = projects.find(p => p.key === e.target.value);
                setProjectName(p?.name || '');
              }}>
              <option value="">Select a project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.key}>{p.name} ({p.key})</option>
              ))}
            </select>
          ) : (
            <input className="input" value={projectKey}
              onChange={e => setProjectKey(e.target.value.toUpperCase())}
              placeholder="e.g. PROJ" />
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Retro Actions Jira Project</label>
          <input
            className="input"
            value={retroKey}
            onChange={e => setRetroKey(e.target.value.toUpperCase())}
            placeholder="RA"
          />
          <p className="input-hint">Project key for retro action items (default: RA)</p>
        </div>

        <div className="input-group">
          <label className="input-label">Slack Channel ID</label>
          <input
            className="input"
            value={slackChannel}
            onChange={e => setSlackChannel(e.target.value)}
            placeholder="C0XXXXXXX"
          />
          <p className="input-hint">Retro summaries will be posted here</p>
        </div>

        <div className="input-group">
          <label className="input-label">Notion Database ID</label>
          <input
            className="input"
            value={notionDb}
            onChange={e => setNotionDb(e.target.value)}
            placeholder="32-character ID"
          />
        </div>

        <div className="input-group">
          <label className="input-label">AI Context — Team Guidelines</label>
          <textarea
            className="input"
            value={playbookCtx}
            onChange={e => setPlaybookCtx(e.target.value)}
            rows={4}
            placeholder="Team-specific rules for AI action suggestions. E.g. 'Mobile team. Backend changes need platform sign-off.'"
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && <div className="toast toast-error mb-4">{error}</div>}

        <div className="flex gap-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary w-full" onClick={handleSave}
            disabled={loading || !name || !projectKey}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewSessionModal({ team, onClose, onCreated }) {
  const [name, setName] = useState(`Sprint Planning - ${new Date().toLocaleDateString()}`);
  const [timer, setTimer] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const session = await api.sessions.create({
        team_id: team.id,
        name,
        timer_seconds: parseInt(timer)
      });
      onCreated(session);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Start Session</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="mb-4">
          <span className="badge badge-purple">{team.name}</span>
        </div>

        <div className="input-group">
          <label className="input-label">Session Name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} autoComplete="off" />
        </div>

        <div className="input-group">
          <label className="input-label">Timer per round (seconds)</label>
          <select className="input" value={timer} onChange={e => setTimer(e.target.value)}>
            <option value="60">60s</option>
            <option value="90">90s (default)</option>
            <option value="120">2 min</option>
            <option value="180">3 min</option>
            <option value="300">5 min</option>
            <option value="0">No timer</option>
          </select>
        </div>

        {error && <div className="toast toast-error mb-4">{error}</div>}

        <div className="flex gap-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary w-full" onClick={handleSubmit} disabled={loading || !name}>
            {loading ? 'Starting...' : 'Start Session'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingPage() {
  return (
    <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', animation: 'pulse 1.5s ease infinite' }}>🃏</div>
        <p className="text-muted mt-4">Loading...</p>
      </div>
    </div>
  );
}
